<template>
  <div
    class="relative bg-gradient-to-br from-white via-gray-50 to-logo-color/5 py-20 md:py-32 overflow-hidden"
  >
    <!-- Background Decorations -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-40 right-20 w-72 h-72 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-20 left-10 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Header Section -->
      <div class="max-w-3xl mx-auto text-center mb-16" data-aos="fade-up">
        <span
          class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-logo-color"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span
            class="text-sm font-semibold text-logo-color uppercase tracking-wider"
            >FAQ</span
          >
        </span>
        <h2
          class="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
        >
          Frequently Asked
          <span class="text-logo-color relative">
            Questions
            <svg
              class="absolute -bottom-2 left-0 w-full"
              height="8"
              viewBox="0 0 200 8"
              fill="none"
            >
              <path
                d="M1 5.5C47.6667 2.16667 141.4 -2.1 199 5.5"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                class="text-logo-color/30"
              />
            </svg>
          </span>
        </h2>
        <p class="text-gray-600 text-base md:text-lg max-w-xl mx-auto">
          Get answers to common questions about our programs and services
        </p>
      </div>

      <!-- FAQ Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-6xl mx-auto">
        <!-- Left Column -->
        <div class="space-y-5" data-aos="fade-right">
          <!-- FAQ Item 1 -->
          <div
            class="group bg-white rounded-2xl transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
            :class="isOpen[1] ? 'ring-2 ring-logo-color' : ''"
          >
            <button
              class="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
              @click="toggleAnswer(1)"
            >
              <h3 class="text-lg font-bold text-gray-900 text-left pr-4">
                What programs does Youth Ally offer?
              </h3>
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="isOpen[1] ? 'bg-logo-color' : 'bg-logo-color/10'"
                >
                  <svg
                    class="w-5 h-5 transform transition-transform duration-300"
                    :class="[
                      isOpen[1] ? 'rotate-180 text-white' : 'text-logo-color',
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :style="{ maxHeight: isOpen[1] ? '300px' : '0px' }"
            >
              <div
                class="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4"
              >
                Youth Ally offers four main programs: Ethical Leadership
                (building integrity-driven leaders), Work Readiness (preparing
                youth for careers), Mentorship & Coaching (guidance from
                industry experts), and Entrepreneurship (turning ideas into
                thriving businesses).
              </div>
            </div>
          </div>

          <!-- FAQ Item 3 -->
          <div
            class="group bg-white rounded-2xl transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
            :class="isOpen[3] ? 'ring-2 ring-logo-color' : ''"
          >
            <button
              class="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
              @click="toggleAnswer(3)"
            >
              <h3 class="text-lg font-bold text-gray-900 text-left pr-4">
                What is the Ethical Leadership program?
              </h3>
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="isOpen[3] ? 'bg-logo-color' : 'bg-logo-color/10'"
                >
                  <svg
                    class="w-5 h-5 transform transition-transform duration-300"
                    :class="[
                      isOpen[3] ? 'rotate-180 text-white' : 'text-logo-color',
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :style="{ maxHeight: isOpen[3] ? '300px' : '0px' }"
            >
              <div
                class="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4"
              >
                The Ethical Leadership program promotes transformative and
                servant leadership among young people. It focuses on building
                integrity-driven leaders who can make ethical decisions and
                positively influence their communities through values-based
                leadership practices.
              </div>
            </div>
          </div>

          <!-- FAQ Item 5 -->
          <div
            class="group bg-white rounded-2xl transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
            :class="isOpen[5] ? 'ring-2 ring-logo-color' : ''"
          >
            <button
              class="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
              @click="toggleAnswer(5)"
            >
              <h3 class="text-lg font-bold text-gray-900 text-left pr-4">
                How can I get involved with Youth Ally?
              </h3>
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="isOpen[5] ? 'bg-logo-color' : 'bg-logo-color/10'"
                >
                  <svg
                    class="w-5 h-5 transform transition-transform duration-300"
                    :class="[
                      isOpen[5] ? 'rotate-180 text-white' : 'text-logo-color',
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :style="{ maxHeight: isOpen[5] ? '300px' : '0px' }"
            >
              <div
                class="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4"
              >
                There are several ways to get involved: Volunteer your time and
                skills, become a Mentor to guide young people, or Donate to
                support our programs. Your contribution helps empower youth
                across Africa to become ethical leaders and create economic
                opportunities.
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="space-y-5" data-aos="fade-left">
          <!-- FAQ Item 2 -->
          <div
            class="group bg-white rounded-2xl transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
            :class="isOpen[2] ? 'ring-2 ring-logo-color' : ''"
          >
            <button
              class="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
              @click="toggleAnswer(2)"
            >
              <h3 class="text-lg font-bold text-gray-900 text-left pr-4">
                What is the Work Readiness program?
              </h3>
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="isOpen[2] ? 'bg-logo-color' : 'bg-logo-color/10'"
                >
                  <svg
                    class="w-5 h-5 transform transition-transform duration-300"
                    :class="[
                      isOpen[2] ? 'rotate-180 text-white' : 'text-logo-color',
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :style="{ maxHeight: isOpen[2] ? '300px' : '0px' }"
            >
              <div
                class="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4"
              >
                The Work Readiness program equips young people with essential
                skills for their career paths. Through practical hands-on
                training, youth gain the competencies needed to be job-ready,
                self-reliant, and able to contribute meaningfully to the
                workforce.
              </div>
            </div>
          </div>

          <!-- FAQ Item 4 -->
          <div
            class="group bg-white rounded-2xl transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
            :class="isOpen[4] ? 'ring-2 ring-logo-color' : ''"
          >
            <button
              class="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
              @click="toggleAnswer(4)"
            >
              <h3 class="text-lg font-bold text-gray-900 text-left pr-4">
                How does mentorship work at Youth Ally?
              </h3>
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="isOpen[4] ? 'bg-logo-color' : 'bg-logo-color/10'"
                >
                  <svg
                    class="w-5 h-5 transform transition-transform duration-300"
                    :class="[
                      isOpen[4] ? 'rotate-180 text-white' : 'text-logo-color',
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :style="{ maxHeight: isOpen[4] ? '300px' : '0px' }"
            >
              <div
                class="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4"
              >
                Our Mentorship & Coaching program connects young people with
                experienced industry experts who provide guidance, support, and
                practical advice. Mentors help youth navigate their personal and
                professional development journeys through regular coaching
                sessions.
              </div>
            </div>
          </div>

          <!-- FAQ Item 6 -->
          <div
            class="group bg-white rounded-2xl transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
            :class="isOpen[6] ? 'ring-2 ring-logo-color' : ''"
          >
            <button
              class="w-full px-6 py-5 flex items-center justify-between focus:outline-none"
              @click="toggleAnswer(6)"
            >
              <h3 class="text-lg font-bold text-gray-900 text-left pr-4">
                What is the Entrepreneurship program about?
              </h3>
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="isOpen[6] ? 'bg-logo-color' : 'bg-logo-color/10'"
                >
                  <svg
                    class="w-5 h-5 transform transition-transform duration-300"
                    :class="[
                      isOpen[6] ? 'rotate-180 text-white' : 'text-logo-color',
                    ]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :style="{ maxHeight: isOpen[6] ? '300px' : '0px' }"
            >
              <div
                class="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4"
              >
                The Entrepreneurship program helps young people transform their
                talents and ideas into thriving, competitive, and ethical
                businesses. Participants learn to create dignified economic
                opportunities for themselves and their communities, with the
                potential to employ other young people.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FinancialFAQ",
  data() {
    return {
      isOpen: {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
      },
    };
  },
  methods: {
    toggleAnswer(id) {
      this.isOpen[id] = !this.isOpen[id];
    },
  },
};
</script>

<style scoped>
/* Modern minimalist FAQ design */
</style>
